/**
 * MergeCandidatesView — /products/merge  합칠 후보 탭(제품 › 제품 관리)
 *
 * 표 하나 — 제품 한 줄. 서버가 이은 묶음(같은 물건 후보)끼리 붙여 두고, 묶음마다 배경 띠를 번갈아 칠한다.
 * 줄: 체크 · 묶음 번호 · 갈래 · 파트넘버 · 디스크립션 · 제품번호. 실물 수는 표에 없다(서버 수는 "대표로 옮길 수" 라 대표 줄이 0 으로 읽힌다).
 * 사람이 줄을 체크하고 [합치기 (n)] 을 누르면 창(openMergeGroupDialog)에서 대표를 고르고 들어갈 곳을 보고 확정한다.
 * 다른 묶음의 줄도 같이 고를 수 있다 — 같은 물건인지는 사람이 정하고, 타입 · 같은 PO · 같은 시리얼은 서버가 막는다.
 * 백엔드 V장 "묶음 합치기 — 화면 다시"(현민 2026-10-26). 둘씩 합치기 창(openMergeDialog)은 PN 정정 · 승격 409 가 쓴다.
 */
import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';
import * as api from '../api/client';
import type { MergeGroup, MergeGroupMember } from '../api/client';
import { findMenuPlace, menuHeading, renderMenuTabs } from '../components/menu';
import { openMergeGroupDialog } from '../components/merge-group-dialog';
import { assert } from '../lib/assert';
import { escapeHtml } from '../lib/cells';

interface MergeCandidatesData {
  groups: MergeGroup[] | null;   // 받은 묶음. null = 아직
  checked: Set<string>;          // 체크한 제품 id — 묶음을 넘어 고를 수 있다
  loading: boolean;
  error: string;
}

const TAB_HREF = '/products/merge';
const MIN_MERGE_COUNT = 2;   // 대표 하나 + 들어갈 것 하나

export class MergeCandidatesView extends SolaView<MergeCandidatesData> {
  override events = {
    click:  { '@merge': 'handleMerge', '@product': 'handleOpenProduct' },
    change: { '@check': 'handleCheck' },
  };

  constructor() {
    super();
    this.data = { groups: null, checked: new Set(), loading: true, error: '' };
  }

  override getTitle(): string {
    return '제품 관리 › 합칠 후보 — HCLTech';
  }

  override onMount(): void {
    void this.load();
  }

  render(): HTMLElement {
    const place = findMenuPlace(TAB_HREF);
    assert(place !== null, `합칠 후보 탭이 메뉴에 없다: ${TAB_HREF} — MENU_GROUPS 제품 › 제품 관리의 탭에 있어야 한다`);
    return htmlSolaWithInstance(this)`
      <div class="page page-merge pv-page">
        <header class="page-header">
          <h1>${menuHeading(place)}</h1>
          ${renderMenuTabs(place)}
        </header>
        <section class="page-body pv-body">${this.renderBody()}</section>
      </div>`;
  }

  /** 묶음 전부 — 체크는 비운다. 실패면 error 글 */
  private async load(): Promise<void> {
    this.data = { ...this.data, loading: true, error: '' };
    this.refreshBody();
    try {
      const groups = await api.fetchMergeGroups();
      this.data = { ...this.data, groups, checked: new Set(), loading: false };
    } catch (e) {
      this.data = { ...this.data, loading: false, error: messageOf(e) };
    }
    this.refreshBody();
  }

  private refreshBody(): void {
    this.refreshRegion('.pv-body', () => this.renderBody());
  }

  /** 도구 줄([합치기 (n)]) + 표 */
  private renderBody(): string {
    const { groups, loading, error } = this.data;
    const list = groups === null ? [] : groups;
    if (list.length === 0) {
      const emptyWord = loading ? '불러오는 중…' : error !== '' ? `불러오지 못했습니다 — ${escapeHtml(error)}` : '합칠 후보가 없습니다';
      return `<p class="text-muted">${emptyWord}</p>`;
    }
    const count = this.data.checked.size;
    const disabled = count < MIN_MERGE_COUNT ? 'disabled' : '';
    const errorLine = error === '' ? '' : `<p class="pc-error">${escapeHtml(error)}</p>`;
    const rows = list.flatMap((group, groupIndex) => group.members.map((member) => this.renderMemberRow(group, member, groupIndex))).join('');
    return `
      ${errorLine}
      <div class="pc-filter-bar">
        <span class="text-muted">비슷한 제품끼리 모아 두었습니다. 합칠 제품을 체크하고 [합치기] 에서 대표를 고릅니다.</span>
        <span class="pv-spacer"></span>
        <button type="button" class="btn-primary" data-action="merge" ${disabled}>합치기 (${count})</button>
      </div>
      <table class="pv-table mg-table">
        <thead><tr><th></th><th>묶음</th><th>갈래</th><th>파트넘버</th><th>디스크립션</th><th>제품번호</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>`;
  }

  /** 줄 하나 — 체크 · 묶음 번호 · 갈래 · 칸들. 홀수 묶음은 띠 */
  private renderMemberRow(group: MergeGroup, member: MergeGroupMember, groupIndex: number): string {
    const kinds = group.kinds.map((kind) => api.MERGE_KIND_WORD[kind]).join(' · ');
    const bChecked = this.data.checked.has(member.id);
    return `
      <tr class="${groupIndex % 2 === 1 ? 'mg-band' : ''}">
        <td><input type="checkbox" data-action="check" data-id="${escapeHtml(member.id)}" ${bChecked ? 'checked' : ''}></td>
        <td class="pl-no">${groupIndex + 1}</td>
        <td class="pl-sub">${escapeHtml(kinds)}</td>
        <td><b class="mono">${escapeHtml(member.option_part === null ? '—' : member.option_part)}</b></td>
        <td class="pl-desc"><div class="pl-clamp">${escapeHtml(member.description === null ? '' : member.description)}</div></td>
        <td><button type="button" class="mg-number-link" data-action="product" data-id="${escapeHtml(member.id)}">${escapeHtml(member.hclt_number)}</button></td>
      </tr>`;
  }

  /** 체크 켜고 끄기 */
  handleCheck(_e: Event, el: HTMLElement): void {
    const id = el.dataset.id ?? '';
    assert(id !== '', '체크는 data-id(제품 id)를 가진다 — renderMemberRow 가 붙였다');
    const checked = new Set(this.data.checked);
    if (el instanceof HTMLInputElement && el.checked) checked.add(id);
    else checked.delete(id);
    this.data = { ...this.data, checked };
    this.refreshBody();
  }

  /** [합치기 (n)] — 창에서 대표를 고르고 확정. 합쳤으면 남은 대표 상세(새 탭) · 다시 불러온다 */
  async handleMerge(): Promise<void> {
    const memberIds = [...this.data.checked];
    assert(memberIds.length >= MIN_MERGE_COUNT, `합치기는 두 줄 이상 체크해야 켜진다 — renderBody 가 버튼을 껐다: ${memberIds.length}`);
    const detail = await openMergeGroupDialog(memberIds, suggestedKeepOf(this.data.groups ?? [], memberIds));
    if (detail === null) return;
    window.open(`/products/${encodeURIComponent(detail.id)}`, '_blank', 'noopener');
    await this.load();
  }

  /** 제품번호 → 제품 상세 새 탭 */
  handleOpenProduct(_e: Event, el: HTMLElement): void {
    const id = el.dataset.id ?? '';
    assert(id !== '', '제품번호 버튼은 data-id(hclt_product id)를 가진다 — renderMemberRow 가 붙였다');
    window.open(`/products/${encodeURIComponent(id)}`, '_blank', 'noopener');
  }
}

/** 창의 첫 대표 — 고른 줄 중 서버 추천 대표(묶음의 keep_id), 없으면 id 가장 작은 것. 여럿이면 id 작은 것 */
export function suggestedKeepOf(groups: readonly MergeGroup[], checkedIds: readonly string[]): string {
  assert(checkedIds.length > 0, '첫 대표는 고른 줄에서 고른다 — 고른 줄이 없으면 창을 열지 않는다');
  const keeps = new Set(groups.map((group) => group.keep_id));
  const recommended = checkedIds.filter((id) => keeps.has(id));
  const pool = recommended.length > 0 ? recommended : checkedIds;
  return pool.reduce((min, id) => (Number(id) < Number(min) ? id : min));
}

function messageOf(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}
